import { grey, red } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  SetFilterModule,
  SideBarModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo } from "react";
import { agApiSizeColumnsToFit } from "../../../../../_src_shared/utils/agGrid/api/agApiSizeColumnsToFit";
import { columnTypesDollar } from "../../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import { isEmpty } from "lodash";
import { columnTypesPercent } from "../../../../../_src_shared/utils/agGrid/columnTypes/columnTypesPercent";

ModuleRegistry.registerModules([
  SideBarModule,
  MultiFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const OriginatorFeesTable = forwardRef(
  (
    {
      rowData,
      loanAmount = 0,
      onRowClicked,
      loading,
      quickFilterText,
      showTotals = true,
    },
    ref,
  ) => {
    const columnDefs = useMemo(() => {
      return [
        {
          headerName: "FEE NAME",
          field: "itemName",
          minWidth: 100,
          cellStyle: { paddingTop: "8px", paddingBottom: "8px" },
        },
        {
          headerName: "AMOUNT",
          field: "itemAmount",
          minWidth: 100,
          width: 140,
          maxWidth: 140,
          type: "dollar",
        },
        {
          headerName: "PERCENT",
          field: "itemPercent",
          minWidth: 100,
          //   flex: 0.8,
          width: 120,
          maxWidth: 120,
          type: "percent",
          cellStyle: { color: grey[600] },
        },
        {
          headerName: "CALCULATED VALUE",
          field: "calculatedValue",
          minWidth: 140,
          width: 160,
          maxWidth: 160,
          valueGetter: (params) => {
            const { node, data } = params || {};
            const { itemAmount, itemPercent, calculatedValue } = data || {};

            // For pinned total row: just use precomputed total
            if (node.rowPinned) {
              return calculatedValue ?? 0;
            }

            if (itemAmount !== null && itemAmount !== undefined) {
              return parseFloat(itemAmount) || 0;
            }

            if (
              itemPercent !== null &&
              itemPercent !== undefined &&
              loanAmount > 0
            ) {
              return loanAmount * itemPercent;
            }

            return 0;
          },
          // valueFormatter: (params) => {
          //   const value = parseFloat(params.value);
          //   if (isNaN(value)) return "$0.00";
          //   return `$${value.toFixed(2)}`;
          // },
          type: "dollar",
          cellStyle: { fontWeight: 600 },
        },
        {
          headerName: "PAYMENT",
          field: "payment",
          minWidth: 120,
          width: 120,
          maxWidth: 120,

          valueFormatter: (params) => {
            const { paidAtClosing, paidOutsideClosing } = params.data;

            if (paidAtClosing) {
              return "At Closing";
            }
            if (paidOutsideClosing) {
              return "Outside";
            }
          },
        },
        {
          headerName: "NOTE",
          field: "itemNote",
          minWidth: 250,
          tooltipValueGetter: (params) => params.value,

          flex: 1,

          wrapText: true,
          autoHeight: true,
          resizable: true,
          cellStyle: {
            lineHeight: "1.4",
          },
        },
      ];
    }, [loanAmount]);

    const handleOnRowClicked = useCallback(
      (params) => {
        if (params.node.rowPinned) return;
        onRowClicked?.(params?.data);
      },
      [onRowClicked],
    );

    const defaultColDef = useMemo(
      () => ({
        filter: true,
        flex: 1,
        cellClass: "ag-cell-vertical-center",
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        resizable: true,
        sortable: true,
      }),
      [],
    );

    const columnTypes = useMemo(
      () => ({
        ...columnTypesDollar(),
        ...columnTypesPercent(),
      }),
      [],
    );

    const onFirstDataRendered = useCallback((params) => {
      agApiSizeColumnsToFit(params);
    }, []);

    // Calculate total fees
    const totalFees = useMemo(() => {
      if (!rowData || !Array.isArray(rowData)) return 0;
      return rowData.reduce((sum, fee) => {
        if (fee.itemAmount !== null && fee.itemAmount !== undefined) {
          return sum + parseFloat(fee.itemAmount);
        }
        if (
          fee.itemPercent !== null &&
          fee.itemPercent !== undefined &&
          loanAmount > 0
        ) {
          return sum + loanAmount * fee.itemPercent;
        }
        return sum;
      }, 0);
    }, [rowData, loanAmount]);

    // Pinned bottom row for totals
    const pinnedBottomRowData = useMemo(() => {
      if (!showTotals || isEmpty(rowData)) return undefined;
      return [
        {
          itemName: "TOTAL FEES",
          calculatedValue: totalFees,
          itemNote: `${rowData?.length || 0} fees applied`,
          isPinnedRow: true,
          //   payment: null,
        },
      ];
    }, [showTotals, totalFees, rowData]);

    const getRowStyle = useCallback((params) => {
      if (params.node.rowPinned) {
        return {
          fontWeight: "bold",
          backgroundColor: red[50],
        };
      }
      return null;
    }, []);

    return (
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={rowData}
        simpleTable
        sx={{ minWidth: "800px" }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          loading={loading}
          columnDefs={columnDefs}
          //   columnDefs={dynamicColumnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          tooltipShowDelay={100}
          onFirstDataRendered={onFirstDataRendered}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          rowHeight={50}
          onRowClicked={handleOnRowClicked}
          pinnedBottomRowData={pinnedBottomRowData}
          getRowStyle={getRowStyle}
        />
      </AgGridTableWrapper>
    );
  },
);

OriginatorFeesTable.displayName = "OriginatorFeesTable";

export default OriginatorFeesTable;
